import React, {useMemo} from "react";
import useInsuranceType from "../Stores/InsuranceStore";
import useCohortType from "../Stores/CohortStore";
import useAgeType from "../Stores/AgeStore";
import useSexType from "../Stores/SexStore";
import useYearType from "../Stores/YearStore";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
} from "recharts";

type Datum = {name: string; value: number | null | undefined};

interface YAxisLabelProps {
  value: string;
  viewBox?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    cx?: number;
    cy?: number;
  };
}
interface ComparatorGraphProps {
  displayValue: number;
  comparator: string;
  graphData: Datum[];
  graphText: string;
  maxValue: number;
  graphColor: string;
  valueNonsig: boolean;
  decimalPoint: number;
  percentDisplay: boolean;
  costDisplay: boolean;
}

const ComparatorGraph: React.FC<ComparatorGraphProps> = ({
  comparator,
  graphData,
  graphText,
  maxValue,
  graphColor,
  valueNonsig,
  decimalPoint,
  percentDisplay,
  costDisplay,
}) => {
  const insuranceList = useInsuranceType((state) => state.insuranceList);
  const cohortList = useCohortType((state) => state.cohortList);
  const ageList = useAgeType((state) => state.ageList);
  const sexList = useSexType((state) => state.sexList);
  const yearList = useYearType((state) => state.yearList);

  const isInsuranceComp = comparator === "Insurance";
  const isCohortComp = comparator === "Cohort";
  const isAgeComp = comparator === "Age";
  const isSexComp = comparator === "Sex";
  const isYearComp = comparator === "Year";

  const formatValue = (v: number) => {
    const prefix = costDisplay ? "$" : "";
    const suffix = percentDisplay ? "%" : "";
    return (
      prefix +
      Number(v)
        .toFixed(decimalPoint)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
      suffix
    );
  };

  const formatTick = (num: number) => {
    if (percentDisplay) return `${num}%`;
    const prefix = costDisplay ? "$" : "";
    if (num >= 1e9)
      return prefix + (num / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
    if (num >= 1e6)
      return prefix + (num / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
    if (num >= 1e3)
      return prefix + (num / 1e3).toFixed(1).replace(/\.0$/, "") + "k";
    return prefix + num.toString();
  };

  const ticks = useMemo(() => {
    const safeMax = Math.max(0, maxValue || 0);
    const step = safeMax / 4;
    return Array.from({length: 5}, (_, i) => Math.round(step * i));
  }, [maxValue]);

  const renderCustomizedLabel = (props: any) => {
    const {x, y, width, value, index} = props;
    if (x == null || y == null || width == null || value == null) return null;

    const datum = graphData?.[index] as any;

    const valText = formatValue(Number(value));

    const cx = Number(x) + Number(width) / 2;
    const topY = Number(y) - 18;

    return (
      <g>
        <text
          className=""
          x={cx}
          y={topY}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={20}
          fill="#000000">
          {valText}
        </text>
      </g>
    );
  };

  const YAxisLabel: React.FC<YAxisLabelProps> = ({value, viewBox}) => {
    if (!viewBox) return null;

    const {x = 0, y = 0, width = 0, height = 0} = viewBox;
    const cx = x + width / 2;
    const cy = y + height / 2;

    // If label is long, split into two lines
    const isLong = value.length > 44;

    let line1 = value;
    let line2 = "";

    if (isLong) {
      // Split at nearest space before 44 chars (cleaner break)
      const breakIndex = value.lastIndexOf(" ", 44);
      if (breakIndex !== -1) {
        line1 = value.slice(0, breakIndex);
        line2 = value.slice(breakIndex + 1);
      } else {
        // If no space found, force split at 44
        line1 = value.slice(0, 44);
        line2 = value.slice(44);
      }
    }

    return (
      <text
        x={cx}
        y={cy - 35}
        textAnchor="middle"
        transform={`rotate(-90, ${cx}, ${cy})`}
        // style={{ fontSize: 18, fontWeight: 300 }}
        className="text-[12px] md:text-[14px] lg:text-[18px] font-normal">
        <tspan x={cx} dy="0">
          {line1}
        </tspan>

        {isLong && (
          <tspan x={cx} dy="1.2em">
            {line2}
          </tspan>
        )}
      </text>
    );
  };
  if (valueNonsig) return <div>Insufficient data</div>;

  return (
    <div
      id="chart-container"
      className="sm:ms-auto mx-auto text-black w-[320px] h-[286px] md:w-[531px] md:h-[430px] lg:w-[482px] lg:h-[390px] xl:h-[350px] xl:w-[700px]">
      <div className="flex w-full justify-center text-[18px]">{graphText}</div>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={graphData}
          margin={{top: 40, right: 20, left: 20, bottom: 5}}>
          <CartesianGrid horizontal vertical={false} strokeDasharray="3 3" />

          <XAxis
            dataKey="name"
            tickFormatter={(tick) => {
              if (isInsuranceComp) return insuranceList?.[tick] ?? tick;
              if (isCohortComp) return cohortList?.[tick] ?? tick;
              if (isAgeComp) return ageList?.[tick] ?? tick;
              if (isSexComp) return sexList?.[tick] ?? tick;
              if (isYearComp) return yearList?.[tick] ?? tick;
              return tick;
            }}
            tick={{fill: "black", fontSize: 18}}
          />

        <YAxis
  type="number"
  domain={[0, Math.max(0, maxValue)]}
  tickFormatter={formatTick}
  ticks={ticks}
  tick={{fill: "black", fontSize: 15}}
  width={30}
/>
          <Bar dataKey="value" fill={graphColor} isAnimationActive>
            <LabelList content={renderCustomizedLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ComparatorGraph;
